import { io, Socket } from "socket.io-client";
import { CACHE_KEYS } from "../constants/cache-keys";
import {
  AIStateUpdate,
  MessageType,
  ServerConnectionStatus,
  SocketState,
} from "../interfaces/all";
import { CacheManager } from "../utils/cache-manager";
import { ErrorHandler } from "../utils/error-handler";

const SOCKET_CONFIG = {
  RECONNECT_ATTEMPTS: 5,
  RECONNECT_DELAY: 1000,
  MAX_RECONNECT_DELAY: 60000,
  TIMEOUT: 10000,
  RECONNECT_DELAY_FACTOR: 1.5,
  PING_INTERVAL: 60000,
  BATCH_DELAY: 100,
  MAX_BATCH_SIZE: 5,
  DEBOUNCE_DELAY: 50,
  COMPRESSION_THRESHOLD: 2048,
} as const;

export const initialSocketState: SocketState = {
  isConnected: false,
  isConnecting: false,
  lastError: null,
  reconnectAttempt: 0,
};

// Tipos para os eventos do socket
export interface SocketEventHandlers {
  onNewMessage?: (data: MessageType & { companyId: string }) => void;
  onNewMessageBatch?: (data: (MessageType & { companyId: string })[]) => void;
  onMessageSending?: (data: {
    to: string;
    type: string;
    content: any;
    timestamp: Date;
    isAIResponse: boolean;
    companyId: string;
  }) => void;
  onMessageError?: (data: {
    to: string;
    type: string;
    content: any;
    error: string;
    timestamp: Date;
    isAIResponse: boolean;
    companyId: string;
  }) => void;
  onMessageStatusUpdate?: (data: {
    messageId: string;
    to: string;
    status: string;
    timestamp: number;
    conversation: any;
    pricing: any;
    recipient_id: string;
    statusHistory: any[];
    companyId: string;
  }) => void;
  onAIStateUpdate?: (data: AIStateUpdate & { companyId: string }) => void;
  onMessageReadStatusUpdate?: (data: {
    messageId: string;
    to: string;
    isRead: boolean;
    readBy: { id: string; name: string } | null;
    readAt: Date | null;
    companyId: string;
  }) => void;
  onAllMessagesRead?: (data: {
    phoneNumber: string;
    readBy: { id: string; name: string };
    readAt: Date;
    count: number;
    companyId: string;
  }) => void;
  onServerConnectionStatus?: (data: ServerConnectionStatus) => void;
  onPong?: () => void;
  onTyping?: (data: {
    phoneNumber: string;
    isTyping: boolean;
    companyId?: string;
  }) => void;
  onBulkJobCompleted?: (data: {
    jobId: string;
    campaignId: string;
    templateName: string;
    result: {
      total: number;
      sent: number;
      failed: number;
      processed: number;
    };
    completedAt: Date;
    duration: number;
    successRate: number;
    companyId: string;
  }) => void;
  onBulkJobFailed?: (data: {
    jobId: string;
    campaignId: string;
    templateName: string;
    error: string;
    completedAt: Date;
    duration: number;
    companyId: string;
  }) => void;
}

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private errorCallback: ((error: unknown) => void) | null = null;
  private stateCallback: ((state: Partial<SocketState>) => void) | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private currentReconnectAttempt = 0;
  private lastSocketUrl: string | null = null;
  private eventHandlers: SocketEventHandlers = {};
  private currentCompanyId: string | null = null;
  private currentUserId: string | null = null;

  // Otimizações de performance
  private messageBuffer: Map<string, any[]> = new Map();
  private bufferTimeouts: Map<string, ReturnType<typeof setTimeout>> =
    new Map();
  private processedMessages: Set<string> = new Set();
  private lastPingTime = 0;
  private connectionQuality = "good"; // 'good', 'poor', 'excellent'

  private constructor() {}

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public setErrorCallback(callback: (error: unknown) => void) {
    this.errorCallback = callback;
  }

  public setStateCallback(callback: (state: Partial<SocketState>) => void) {
    this.stateCallback = callback;
  }

  public setEventHandlers(handlers: SocketEventHandlers) {
    const mergedHandlers: SocketEventHandlers = {};
    Object.keys(handlers).forEach((key) => {
      const handler = handlers[key as keyof SocketEventHandlers];
      if (handler !== undefined) {
        (mergedHandlers as any)[key] = handler;
      }
    });
    this.eventHandlers = { ...this.eventHandlers, ...mergedHandlers };
  }

  public removeEventHandlers() {
    this.eventHandlers = {};
  }

  private updateState(updates: Partial<SocketState>) {
    if (this.stateCallback) {
      this.stateCallback(updates);
    }
  }

  private handleError(error: unknown) {
    const errorResponse = ErrorHandler.handleApiError(error);
    console.error("Erro no socket:", errorResponse);

    this.updateState({
      lastError: errorResponse.message,
    });

    if (this.errorCallback) {
      this.errorCallback(errorResponse);
    }

    if (errorResponse.type === "authentication") {
      this.disconnect();
      CacheManager.remove(CACHE_KEYS.AUTH.TOKEN);
    }
  }

  // Método otimizado para processar mensagens em batch
  private processMessageBatch(eventType: string, data: any): void {
    const key = `${eventType}:${data.companyId || "global"}`;

    if (!this.messageBuffer.has(key)) {
      this.messageBuffer.set(key, []);
    }

    const messageBuffer = this.messageBuffer.get(key);
    if (messageBuffer) {
      messageBuffer.push(data);
    }

    // Limpar timeout existente
    const existingTimeout = this.bufferTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Processar imediatamente se atingir o limite
    const currentBuffer = this.messageBuffer.get(key);
    if (currentBuffer && currentBuffer.length >= SOCKET_CONFIG.MAX_BATCH_SIZE) {
      this.flushMessageBuffer(key);
    } else {
      // Agendar processamento
      const timeout = setTimeout(() => {
        this.flushMessageBuffer(key);
      }, SOCKET_CONFIG.BATCH_DELAY);

      this.bufferTimeouts.set(key, timeout);
    }
  }

  private flushMessageBuffer(key: string): void {
    const parts = key.split(":");
    const eventType = parts[0] || "unknown";
    const messages = this.messageBuffer.get(key);

    if (!messages || messages.length === 0) return;

    // Remover timeout
    const existingTimeout = this.bufferTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.bufferTimeouts.delete(key);
    }

    // Processar mensagens
    if (messages.length === 1) {
      this.processSingleMessage(eventType, messages[0]);
    } else {
      this.processBatchMessages(eventType, messages);
    }

    this.messageBuffer.delete(key);
  }

  private processSingleMessage(eventType: string, data: any): void {
    const messageId =
      data._id || data.messageId || `${Date.now()}_${Math.random()}`;

    // Evitar processamento duplicado
    if (this.processedMessages.has(messageId)) {
      return;
    }

    this.processedMessages.add(messageId);

    // Limitar tamanho do Set para evitar vazamento de memória
    if (this.processedMessages.size > 1000) {
      const firstKey = this.processedMessages.values().next().value;
      if (firstKey) {
        this.processedMessages.delete(firstKey);
      }
    }

    this.callEventHandler(eventType, data);
  }

  private processBatchMessages(eventType: string, messages: any[]): void {
    // Para batches, usar evento específico se disponível
    const batchEventType = `${eventType}Batch`;
    if (this.eventHandlers[batchEventType as keyof SocketEventHandlers]) {
      this.callEventHandler(batchEventType, messages);
    } else {
      // Processar individualmente
      messages.forEach((message) =>
        this.processSingleMessage(eventType, message),
      );
    }
  }

  private callEventHandler(eventType: string, data: any): void {
    const handler = this.eventHandlers[
      eventType as keyof SocketEventHandlers
    ] as any;
    if (handler && typeof handler === "function") {
      try {
        handler(data);
      } catch (error) {
        console.error(
          `[Socket] Erro no handler do evento ${eventType}:`,
          error,
        );
      }
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.currentReconnectAttempt >= SOCKET_CONFIG.RECONNECT_ATTEMPTS) {
      this.updateState({
        isConnecting: false,
        lastError: "Número máximo de tentativas de reconexão atingido",
      });
      return;
    }

    const delay = Math.min(
      SOCKET_CONFIG.RECONNECT_DELAY *
        Math.pow(
          SOCKET_CONFIG.RECONNECT_DELAY_FACTOR,
          this.currentReconnectAttempt,
        ),
      SOCKET_CONFIG.MAX_RECONNECT_DELAY,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.currentReconnectAttempt++;
      this.updateState({
        isConnecting: true,
        reconnectAttempt: this.currentReconnectAttempt,
      });
      if (this.lastSocketUrl) {
        this.connect(this.lastSocketUrl);
      }
    }, delay);
  }

  private setupEventListeners() {
    if (!this.socket) return;

    // Eventos de conexão
    this.socket.on("connect", () => {
      this.currentReconnectAttempt = 0;
      this.updateState({
        isConnected: true,
        isConnecting: false,
        lastError: null,
        reconnectAttempt: 0,
      });

      // Reautenticar e entrar na sala da empresa se necessário
      if (this.currentUserId) {
        this.socket?.emit("authenticate", { userId: this.currentUserId });
      }
      if (this.currentCompanyId) {
        this.socket?.emit("joinCompany", { companyId: this.currentCompanyId });
      }

      // Iniciar ping
      this.startPing();
    });

    this.socket.on("disconnect", (reason: string) => {
      this.updateState({
        isConnected: false,
        isConnecting: false,
      });

      this.stopPing();

      if (
        reason === "io server disconnect" ||
        reason === "io client disconnect"
      ) {
        this.disconnect();
      } else {
        this.scheduleReconnect();
      }
    });

    this.socket.on("connect_error", (error: Error) => {
      console.error("[Socket] Erro na conexão:", error);
      this.handleError(error);
      this.scheduleReconnect();
    });

    this.socket.on("error", (error: Error) => {
      console.error("[Socket] Erro geral:", error);
      this.handleError(error);
    });

    // Eventos de mensagem otimizados
    this.socket.on(
      "newMessage",
      (data: MessageType & { companyId: string }) => {
        this.processMessageBatch("onNewMessage", data);
      },
    );

    this.socket.on(
      "newMessageBatch",
      (data: (MessageType & { companyId: string })[]) => {
        if (this.eventHandlers.onNewMessageBatch) {
          this.eventHandlers.onNewMessageBatch(data);
        }
      },
    );

    this.socket.on("messageSending", (data: any) => {
      this.processMessageBatch("onMessageSending", data);
    });

    this.socket.on("messageError", (data: any) => {
      // Erros são processados imediatamente
      this.callEventHandler("onMessageError", data);
    });

    this.socket.on("messageStatusUpdate", (data: any) => {
      // Processar imediatamente para evitar delay na atualização do status
      this.callEventHandler("onMessageStatusUpdate", data);
    });

    this.socket.on(
      "aiStateUpdate",
      (data: AIStateUpdate & { companyId: string }) => {
        this.callEventHandler("onAIStateUpdate", data);
      },
    );

    this.socket.on("messageReadStatusUpdate", (data: any) => {
      // Processar imediatamente para evitar delay na atualização do status
      this.callEventHandler("onMessageReadStatusUpdate", data);
    });

    this.socket.on("allMessagesRead", (data: any) => {
      // Processar imediatamente para evitar delay na atualização do status
      this.callEventHandler("onAllMessagesRead", data);
    });

    this.socket.on("serverConnectionStatus", (data: ServerConnectionStatus) => {
      this.callEventHandler("onServerConnectionStatus", data);
    });

    this.socket.on("pong", () => {
      const now = Date.now();
      const pingTime = now - this.lastPingTime;
      this.lastPingTime = now;

      // Atualizar qualidade da conexão baseado no ping
      if (pingTime < 50) {
        this.connectionQuality = "excellent";
      } else if (pingTime < 100) {
        this.connectionQuality = "good";
      } else {
        this.connectionQuality = "poor";
      }

      this.callEventHandler("onPong", undefined);
    });

    // Evento de digitação
    this.socket.on("typing", (data: any) => {
      this.callEventHandler("onTyping", data);
    });

    this.socket.on("chatTyping", (data: any) => {
      this.callEventHandler("onTyping", data);
    });

    // Eventos de envio em massa
    this.socket.on("bulkJobCompleted", (data: any) => {
      this.callEventHandler("onBulkJobCompleted", data);
    });

    this.socket.on("bulkJobFailed", (data: any) => {
      this.callEventHandler("onBulkJobFailed", data);
    });
  }

  private startPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    this.pingInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.lastPingTime = Date.now();
        this.socket.emit("ping");
      }
    }, SOCKET_CONFIG.PING_INTERVAL);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  public connect(
    socketUrl: string,
    userId?: string,
    companyId?: string,
  ): Socket | null {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.lastSocketUrl = socketUrl;
    this.currentUserId = userId || null;
    this.currentCompanyId = companyId || null;

    this.updateState({
      isConnecting: true,
      lastError: null,
      reconnectAttempt: this.currentReconnectAttempt,
    });

    try {
      this.socket = io(socketUrl, {
        withCredentials: true,
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: SOCKET_CONFIG.RECONNECT_ATTEMPTS,
        reconnectionDelay: SOCKET_CONFIG.RECONNECT_DELAY,
        reconnectionDelayMax: SOCKET_CONFIG.MAX_RECONNECT_DELAY,
        timeout: SOCKET_CONFIG.TIMEOUT,
        forceNew: true,
        // Otimizações de performance
        upgrade: true,
        rememberUpgrade: true,
        perMessageDeflate: {
          threshold: SOCKET_CONFIG.COMPRESSION_THRESHOLD,
        },
      });

      this.setupEventListeners();

      return this.socket;
    } catch (error) {
      console.error("[Socket] Erro ao inicializar socket:", error);
      this.updateState({
        isConnecting: false,
        lastError:
          error instanceof Error ? error.message : "Erro ao inicializar socket",
      });
      this.scheduleReconnect();
      return null;
    }
  }

  public authenticate(userId: string) {
    this.currentUserId = userId;
    if (this.socket?.connected) {
      this.socket.emit("authenticate", { userId });
    }
  }

  public joinCompany(companyId: string) {
    this.currentCompanyId = companyId;
    if (this.socket?.connected) {
      this.socket.emit("joinCompany", { companyId });
    }
  }

  public leaveCompany(companyId: string) {
    if (this.socket?.connected) {
      this.socket.emit("leaveCompany", { companyId });
    }
    if (this.currentCompanyId === companyId) {
      this.currentCompanyId = null;
    }
  }

  public disconnect() {
    this.stopPing();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Limpar buffers
    this.messageBuffer.clear();
    this.bufferTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.bufferTimeouts.clear();
    this.processedMessages.clear();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.lastSocketUrl = null;
    this.currentReconnectAttempt = 0;
    this.currentUserId = null;
    this.currentCompanyId = null;
    this.updateState(initialSocketState);
  }

  public getSocket(): Socket | null {
    return this.socket;
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }

  public getCurrentCompanyId(): string | null {
    return this.currentCompanyId;
  }

  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  // Método para obter estatísticas de performance
  public getPerformanceStats(): any {
    return {
      isConnected: this.isConnected(),
      connectionQuality: this.connectionQuality,
      bufferedMessages: Array.from(this.messageBuffer.values()).reduce(
        (acc, messages) => acc + messages.length,
        0,
      ),
      processedMessagesCount: this.processedMessages.size,
      currentCompanyId: this.currentCompanyId,
      currentUserId: this.currentUserId,
    };
  }
}

export default SocketService.getInstance();
